import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { fetchCollectionV2, getLocalized } from "../lib/cms";
import { CONTACT_INFO } from "../data";

export default function GalleryPage() {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("Alle");
  const [activeImage, setActiveImage] = useState(null); // Lightbox state: { url, title, idx, list }

  useEffect(() => {
    async function loadGalleries() {
      try {
        const response = await fetchCollectionV2("gallery", { limit: 50 });
        setGalleries(response.items || []);
      } catch (err) {
        console.error("Fejl ved hentning af galleri:", err);
      } finally {
        setLoading(false);
      }
    }
    loadGalleries();
  }, []);

  // Extract unique categories
  const categories = ["Alle", ...new Set(galleries.map((g) => getLocalized(g.category)))];

  // Filter galleries based on active category
  const filteredGalleries = activeCategory === "Alle"
    ? galleries
    : galleries.filter((g) => getLocalized(g.category) === activeCategory);

  // Flatten images for the selected category to show a beautiful unified grid
  const allImages = filteredGalleries.flatMap((g) => 
    (g.images || []).map((img) => ({
      url: img,
      title: getLocalized(g.title),
      category: getLocalized(g.category)
    }))
  );

  // Lightbox navigation
  const openLightbox = (img, idx) => {
    setActiveImage({ ...img, idx });
  };

  const closeLightbox = () => {
    setActiveImage(null);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    if (activeImage) {
      const nextIdx = (activeImage.idx + 1) % allImages.length;
      setActiveImage({ ...allImages[nextIdx], idx: nextIdx });
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (activeImage) {
      const prevIdx = (activeImage.idx - 1 + allImages.length) % allImages.length;
      setActiveImage({ ...allImages[prevIdx], idx: prevIdx });
    }
  };

  return (
    <div className="animate-in fade-in duration-500">
      <Helmet>
        <title>Galleri | Bachmann Tømrer</title>
        <meta name="description" content="Se billeder af vores udførte tømrerarbejde i Silkeborg, Ry og Kjellerup. Vi bygger træterrasser, lægger tage, renoverer huse og meget mere." />
        <meta property="og:title" content="Galleri | Bachmann Tømrer" />
        <meta property="og:description" content="Se billeder af vores udførte tømrerarbejde i Silkeborg, Ry og Kjellerup. Vi bygger træterrasser, lægger tage, renoverer huse og meget mere." />
      </Helmet>

      {/* Page Hero */}
      <section className="relative py-24 overflow-hidden bg-brand-graphite text-white">
        {/* Background Image with Dark Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url('https://assets.auxodev.dk/assets/4308d28a-24e2-4c25-a37f-97d46b6f1512/image00038-scaled.jpeg')`,
          }}
        />
        <div className="absolute inset-0 bg-brand-graphite/75" />
        
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center z-10">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-beige">Vores udførte projekter</span>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white mt-2">
            Galleri & referencer
          </h1>
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-white/70">
            <Link to="/" className="hover:text-brand-beige transition-colors">Forside</Link>
            <i className="fa-solid fa-chevron-right text-[8px]" />
            <span className="text-white font-medium">Galleri</span>
          </div>
        </div>
      </section>

      {/* Gallery Grid & Filter */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-brown border-t-transparent" />
            </div>
          ) : (
            <div className="space-y-12">
              {/* Category Filter Tabs */}
              <div className="flex flex-wrap items-center justify-center gap-2 border-b border-brand-brown/10 pb-6">
                {categories.map((cat, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveCategory(cat)}
                    className={`rounded-full px-5 py-2.5 text-xs font-semibold tracking-wider uppercase transition-all duration-200 ${
                      activeCategory === cat
                        ? "bg-brand-brown text-white shadow-md"
                        : "bg-brand-beige text-brand-graphite/80 hover:bg-brand-beige-dark"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Images Grid */}
              {allImages.length > 0 ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {allImages.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => openLightbox(img, idx)}
                      className="group relative aspect-square overflow-hidden rounded-2xl border border-brand-brown/10 bg-brand-beige shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 focus:outline-none"
                    >
                      <img
                        src={`${img.url}?width=500&quality=80&format=auto`}
                        alt={img.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-graphite/80 via-brand-graphite/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex flex-col justify-end p-4 text-left">
                        <span className="inline-block rounded bg-brand-brown/90 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white mb-1.5 w-fit">
                          {img.category}
                        </span>
                        <h3 className="font-serif text-sm font-bold text-white leading-tight">
                          {img.title}
                        </h3>
                        <span className="text-[10px] text-white/70 mt-1 flex items-center gap-1">
                          Se i stor visning <i className="fa-solid fa-magnifying-glass text-[8px]" />
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 text-brand-graphite/50">
                  Ingen billeder fundet i denne kategori.
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-beige-dark/30 border-t border-brand-brown/5">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-brand-graphite mb-4">
            Har du et lignende projekt i tankerne?
          </h2>
          <p className="text-xs sm:text-sm text-brand-graphite/70 max-w-xl mx-auto mb-8 leading-relaxed">
            Vi står altid klar til at tage en uforpligtende snak om dine ønsker og give et skræddersyet tilbud.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto rounded-full bg-brand-brown px-6 py-3 text-xs font-bold uppercase tracking-wider text-white shadow-md hover:bg-brand-brown-dark"
            >
              Kontakt os i dag
            </Link>
            <a
              href={`tel:${CONTACT_INFO.phoneRaw}`}
              className="w-full sm:w-auto rounded-full border border-brand-brown/30 bg-white px-6 py-3 text-xs font-bold uppercase tracking-wider text-brand-brown hover:bg-brand-beige"
            >
              Ring {CONTACT_INFO.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImage && (
        <div
          onClick={closeLightbox}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 animate-in fade-in duration-300"
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-brand-wood text-3xl focus:outline-none z-50"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark" />
          </button>

          {/* Prev button */}
          <button
            onClick={prevImage}
            className="absolute left-4 text-white hover:text-brand-wood text-4xl focus:outline-none z-50 p-2"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left" />
          </button>

          {/* Image Container */}
          <div className="relative max-h-[85vh] max-w-[90vw] flex flex-col items-center justify-center">
            <img
              src={activeImage.url}
              alt={activeImage.title}
              className="max-h-[75vh] max-w-full object-contain rounded-lg shadow-2xl select-none"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="text-center mt-4 text-white space-y-1">
              <h4 className="font-serif text-base font-bold">{activeImage.title}</h4>
              <p className="text-white/60 text-xs font-sans">
                Kategori: {activeImage.category} · Billede {activeImage.idx + 1} af {allImages.length}
              </p>
            </div>
          </div>

          {/* Next button */}
          <button
            onClick={nextImage}
            className="absolute right-4 text-white hover:text-brand-wood text-4xl focus:outline-none z-50 p-2"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right" />
          </button>
        </div>
      )}
    </div>
  );
}
